import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

const LANG_STORAGE_KEY = 'appLang';
export const SUPPORTED_LANGS = ['en', 'de'];

export function normalizeSupportedLang(lang: string | null | undefined): string | undefined {
  if (!lang) {
    return undefined;
  }

  const normalized = lang.trim().toLowerCase().split('-')[0];
  return SUPPORTED_LANGS.includes(normalized) ? normalized : undefined;
}

export function pickInitialLang(
  queryLang: string | null,
  storedLang: string | null,
  browserLangs: readonly string[],
): string {
  return normalizeSupportedLang(queryLang)
    ?? normalizeSupportedLang(storedLang)
    ?? browserLangs.map(lang => normalizeSupportedLang(lang)).find((lang): lang is string => Boolean(lang))
    ?? 'en';
}

@Injectable({
  providedIn: 'root',
})
export class AppTranslateService {
  constructor(private translate: TranslateService) {
    this.translate.setDefaultLang('en');

    // Check ?lang= first, then localStorage, then browser language, then English.
    const params = new URLSearchParams(window.location.search);
    const queryLang = params.get('lang');

    const browserLangs = typeof navigator === 'undefined'
      ? []
      : [...(navigator.languages ?? []), navigator.language].filter((lang): lang is string => Boolean(lang));
    const lang = pickInitialLang(queryLang, localStorage.getItem(LANG_STORAGE_KEY), browserLangs);

    localStorage.setItem(LANG_STORAGE_KEY, lang);
    this.translate.use(lang);
  }

  get currentLang(): string {
    return this.translate.currentLang || 'en';
  }

  setLang(lang: string): void {
    if (!SUPPORTED_LANGS.includes(lang)) return;
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    this.translate.use(lang);
  }

  detectFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const lang = normalizeSupportedLang(params.get('lang'));
    if (lang) this.setLang(lang);
  }
}
